import { msalInstance, apiTokenRequest, InteractionRequiredAuthError } from '../authConfig';

/**
 * TokenRefreshService - Handles silent token acquisition and refresh
 * This service manages Microsoft Entra token lifecycle without user interaction
 */
class TokenRefreshService {
  constructor() {
    this.tokenRefreshPromise = null;
    this.isRefreshing = false;
  }

  /**
   * Get a valid access token - refreshes if needed
   * Uses silent flow to acquire token without user interaction
   * 
   * @returns {Promise<string>} Valid access token
   * @throws {Error} If token acquisition fails
   */
  async getValidToken() {
    try {
      let account = msalInstance.getActiveAccount();

      // If no active account, try to get the first account from cache
      // This handles the case where token exists but account isn't set as "active"
      if (!account) {
        console.log('⚠ No active account found, checking for cached accounts...');
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length > 0) {
          account = accounts[0];
          console.log('✓ Found cached account, setting as active');
          // Note: We don't force setActiveAccount here - MSAL handles this
        } else {
          throw new Error('No account found. User must be logged in.');
        }
      }

      // First, try to get token from cache (most efficient)
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...apiTokenRequest,
          account: account
        });

        console.log('✓ Token acquired from cache or silent refresh');
        return response.accessToken;
      } catch (error) {
        // If silent acquisition fails with InteractionRequiredAuthError, 
        // it means the token has truly expired and needs interactive refresh
        if (error instanceof InteractionRequiredAuthError) {
          console.warn('⚠ Silent token acquisition failed, attempting interactive refresh...');

          // Use interactive flow with redirect (user won't see login if they have valid session)
          const response = await msalInstance.acquireTokenPopup(apiTokenRequest);
          console.log('✓ Token refreshed via popup');
          return response.accessToken;
        }

        throw error;
      }
    } catch (error) {
      console.error('✗ Token refresh failed:', error.message);
      throw error;
    }
  }

  /**
   * Refresh token silently with deduplication
   * If multiple requests come in during refresh, they wait for the first refresh to complete
   * 
   * @returns {Promise<string>} Valid access token
   */
  async refreshTokenSilent() {
    // If already refreshing, wait for existing promise
    if (this.isRefreshing) {
      console.log('⏳ Token refresh already in progress, waiting...');
      return this.tokenRefreshPromise;
    }

    this.isRefreshing = true;
    this.tokenRefreshPromise = this.getValidToken()
      .finally(() => {
        this.isRefreshing = false;
        this.tokenRefreshPromise = null;
      });

    return this.tokenRefreshPromise;
  }

  /**
   * Clear cached tokens to force fresh authentication
   * Use this when user explicitly logs out
   */
  clearTokenCache() {
    const account = msalInstance.getActiveAccount();
    if (account) {
      msalInstance.clearCache();
      console.log('✓ Token cache cleared');
    }
  }

  /**
   * Check if token is about to expire (within 5 minutes)
   * @param {string} token - Access token to check
   * @returns {boolean} True if token is expiring soon
   */
  isTokenExpiringSoon(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiryTime - now;

      // Return true if token expires within 5 minutes (300000ms)
      return timeUntilExpiry < 300000;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true; // Assume expiring if we can't parse
    }
  }
}

// Export singleton instance
export default new TokenRefreshService();
